import { type UseQueryOptions, useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'
import { companyHeader, unwrap } from './api-internal'
import type {
  Account,
  FiscalYearWithPeriods,
  FixedAsset,
  FixedAssetCategory,
  FixedAssetDepreciation,
} from './api-types'

export function useFixedAssets(
  companyId: string | null,
  options?: Omit<UseQueryOptions<FixedAsset[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FixedAsset[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'fixed-assets'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<FixedAsset[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/fixed-assets', {
        headers: companyHeader(companyId),
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load fixed assets')
      return unwrap<FixedAsset[]>(response.data ?? {}, 'fixed-assets') ?? []
    },
  })
}

export function useFixedAssetCategories(
  companyId: string | null,
  options?: Omit<UseQueryOptions<FixedAssetCategory[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FixedAssetCategory[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'asset-categories'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<FixedAssetCategory[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/asset-categories', {
        headers: companyHeader(companyId),
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load categories')
      return unwrap<FixedAssetCategory[]>(response.data ?? {}, 'asset-categories') ?? []
    },
  })
}

/**
 * The chart of accounts — the disposal dialog's bank-account picker.
 *
 * Same endpoint, same query key and same shape as `features/accounting/api.ts`
 * `useAccounts`, per the repo rule: two features may call the same endpoint,
 * and when they share a query key they must store the same shape.
 */
export function useAccounts(
  companyId: string | null,
  options?: Omit<UseQueryOptions<Account[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<Account[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'accounts'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<Account[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/accounts', {
        headers: companyHeader(companyId),
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load accounts')
      return unwrap<Account[]>(response.data ?? {}, 'accounts') ?? []
    },
  })
}

export function useFixedAsset(
  companyId: string | null,
  assetId: string | null,
  options?: Omit<UseQueryOptions<FixedAsset, Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FixedAsset, Error>({
    queryKey: ['companies', companyId, 'accounting', 'fixed-assets', assetId] as const,
    enabled: Boolean(companyId && assetId),
    ...options,
    queryFn: async (): Promise<FixedAsset> => {
      if (!companyId || !assetId) throw new Error('Missing company or asset')
      const response = await api.GET('/accounting/fixed-assets/{asset}', {
        params: { path: { asset: assetId } },
        headers: companyHeader(companyId),
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load the asset')
      return unwrap<FixedAsset>(response.data ?? {}, 'fixed-assets')
    },
  })
}

export function useFixedAssetDepreciations(
  companyId: string | null,
  assetId: string | null,
  options?: Omit<UseQueryOptions<FixedAssetDepreciation[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FixedAssetDepreciation[], Error>({
    queryKey: [
      'companies',
      companyId,
      'accounting',
      'fixed-assets',
      assetId,
      'depreciations',
    ] as const,
    enabled: Boolean(companyId && assetId),
    ...options,
    queryFn: async (): Promise<FixedAssetDepreciation[]> => {
      if (!companyId || !assetId) return []
      const response = await api.GET('/accounting/fixed-assets/{asset}/depreciations', {
        params: { path: { asset: assetId } },
        headers: companyHeader(companyId),
      })
      if (response.error)
        throw new Error(response.error.message ?? 'Failed to load depreciation schedule')
      return (
        unwrap<FixedAssetDepreciation[]>(response.data ?? {}, 'fixed-asset-depreciations') ?? []
      )
    },
  })
}

/**
 * Fiscal years with their periods, for the depreciation run's period picker —
 * same endpoint, key and shape as `features/accounting/api.ts` `useFiscalYears`.
 */
export function useFiscalYears(
  companyId: string | null,
  options?: Omit<UseQueryOptions<FiscalYearWithPeriods[], Error>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FiscalYearWithPeriods[], Error>({
    queryKey: ['companies', companyId, 'accounting', 'fiscal-periods'] as const,
    enabled: Boolean(companyId),
    ...options,
    queryFn: async (): Promise<FiscalYearWithPeriods[]> => {
      if (!companyId) return []
      const response = await api.GET('/accounting/fiscal-periods', {
        headers: companyHeader(companyId),
      })
      if (response.error) throw new Error(response.error.message ?? 'Failed to load periods')
      return unwrap<FiscalYearWithPeriods[]>(response.data ?? {}, 'fiscal-periods') ?? []
    },
  })
}
